import React, { FC, ReactElement, RefObject, useContext, useRef } from 'react'
import Dropzone, { DropzoneState } from 'react-dropzone'
import {
    Controller,
    ControllerRenderProps,
    useFormContext,
    UseFormMethods,
    ValidateResult
} from '../../../../Vendor/react-hook-form/dist/index.ie11'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext } from '../../Contexts'
import { FileUploadLanguage } from '../../../../../i18n'
import { COMMA, EMPTY_STRING } from '../../../../NonReact/constants/Common'
import { isStringNotEmpty, IsVarEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { BackgroundMode, FileUploadDimensions } from '../../../CMEAddModify/Types'
import { convertBytesToClosestUnit } from '../../Utils/FileUtils'
import { Rules } from '../../Types/Types'
import IconButton, { IconButtonType } from '../IconButton'
import { IconName } from '../Icon'
import { colourContrast } from '../../../../NonReact/utils/ColourUtils'
import { SLASH } from '../../Constants/Separators'
import { CommonFormContext, FormContextType } from '../../Contexts/CommonFormContext'

export enum AllowedTypes {
    Image = 'image/*',
    Video = 'video/*'
}

const TYPE_VIDEO = 'video' as const
const TYPE_IMAGE = 'image' as const

type Props = {
    name: string
    typeFieldName?: string
    label: string
    allowedTypes?: string[]
    maxFileSize?: number
    dimensions?: FileUploadDimensions
    disabled?: boolean
    rules?: Rules
    bgMode?: BackgroundMode
    primaryColour?: string
}

export const FileUpload: FC<Props> = ({
    name,
    typeFieldName,
    label,
    allowedTypes = [AllowedTypes.Image],
    maxFileSize,
    dimensions,
    disabled,
    rules,
    bgMode,
    primaryColour
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(FileUploadLanguage.NAME)

    const commonFormContext: Nullable<FormContextType> = useContext(CommonFormContext)

    const { control, watch, setValue }: UseFormMethods = useFormContext()

    const fileValue: File = watch(name)

    const fileValueInitial: RefObject<Nullable<File>> = useRef(fileValue)

    const validateFileDimensions = (file?: File | string): Promise<ValidateResult> =>
        new Promise((resolve: (value: ValidateResult | PromiseLike<ValidateResult>) => void): void => {
            if (IsVarEmpty(file) || typeof file === 'string') {
                resolve(undefined)
                return
            }

            const isImage: boolean = file.type.includes(TYPE_IMAGE)
            const element: HTMLImageElement | HTMLVideoElement = isImage
                ? new Image()
                : document.createElement(TYPE_VIDEO)

            const onLoad = (): void => {
                const width: number = isImage
                    ? (element as HTMLImageElement).width
                    : (element as HTMLVideoElement).videoWidth
                const height: number = isImage
                    ? (element as HTMLImageElement).height
                    : (element as HTMLVideoElement).videoHeight

                if (width > (dimensions?.max?.width ?? Infinity) || height > (dimensions?.max?.height ?? Infinity)) {
                    return resolve(
                        language.getInterpolated(
                            FileUploadLanguage.FileUpload_Validation_MaxDimensions,
                            dimensions?.max?.width,
                            dimensions?.max?.height
                        )
                    )
                }

                if (width < (dimensions?.min?.width ?? 0) || height < (dimensions?.min?.height ?? 0)) {
                    return resolve(
                        language.getInterpolated(
                            FileUploadLanguage.FileUpload_Validation_MinDimensions,
                            dimensions?.min?.width,
                            dimensions?.min?.height
                        )
                    )
                }

                return resolve(true)
            }

            if (isImage) {
                const imageElement: HTMLImageElement = element as HTMLImageElement

                imageElement.onload = onLoad

                const reader: FileReader = new FileReader()

                reader.onload = (e: ProgressEvent<FileReader>): void => {
                    imageElement.src = e.target?.result as string
                }
                reader.readAsDataURL(file)
            } else {
                const videoElement: HTMLVideoElement = element as HTMLVideoElement

                videoElement.preload = 'metadata'

                element.onloadedmetadata = onLoad
                element.src = URL.createObjectURL(file)
            }
        })

    const previewDisplay = (value: Nullable<unknown>): ReactElement => {
        const imageName: string =
            value instanceof File
                ? value.name
                : `${value as string}`.substring(`${value as string}`.lastIndexOf(SLASH) + 1)
        const fileLink: string = value instanceof File ? URL.createObjectURL(value) : `${value as string}`

        let isImage: boolean = true

        if (typeof value === 'string') {
            isImage =
                isStringNotEmpty(typeFieldName) && isStringNotEmpty(watch(typeFieldName))
                    ? !(watch(typeFieldName) === 'video')
                    : true
        } else if (value instanceof File) {
            isImage = value.type.includes(TYPE_IMAGE)
        }

        return value instanceof File || (typeof value === 'string' && isStringNotEmpty(value)) ? (
            <>
                <p className='fucpc-file-name'>
                    {language.getInterpolated(FileUploadLanguage.FileUpload_SelectedFile, imageName)}
                </p>
                <aside className='fuc-preview-container'>
                    {isImage ? (
                        <img
                            className='fucpc-preview'
                            src={fileLink}
                            alt={`${imageName} Preview`}
                            style={{
                                height: dimensions?.max?.height
                            }}
                        />
                    ) : (
                        <video
                            className='fucpc-preview'
                            src={fileLink}
                            controls
                            style={{
                                height: dimensions?.max?.height
                            }}
                        />
                    )}
                </aside>
            </>
        ) : (
            <></>
        )
    }

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={fileValueInitial.current ?? EMPTY_STRING}
            disabled={disabled}
            rules={{
                ...rules,
                validate: {
                    ...rules?.validate,
                    ...(isVarNotEmpty(maxFileSize)
                        ? {
                              maxFileSize: (value: File): ValidateResult => {
                                  if (value.size > maxFileSize) {
                                      return language.getInterpolated(
                                          FileUploadLanguage.FileUpload_Validation_OverMaxSize,
                                          convertBytesToClosestUnit(maxFileSize)
                                      )
                                  }

                                  return undefined
                              }
                          }
                        : {}),
                    ...(isVarNotEmpty(dimensions)
                        ? {
                              dimensions: validateFileDimensions
                          }
                        : {})
                }
            }}
            render={({ onChange, value }: ControllerRenderProps): ReactElement => (
                <section className={`file-upload-container ${isStringNotEmpty(bgMode) ? `${bgMode}-bg-colour` : ''}`}>
                    <div className='fuc-label'>{label}</div>
                    <Dropzone
                        onDrop={(acceptedFiles: File[]): void => {
                            if (acceptedFiles.length === 1) {
                                if (isStringNotEmpty(typeFieldName)) {
                                    setValue(
                                        typeFieldName,
                                        acceptedFiles[0].type.includes(TYPE_IMAGE) ? TYPE_IMAGE : TYPE_VIDEO
                                    )
                                }

                                onChange(acceptedFiles[0])
                                commonFormContext?.handleFormChange?.(name)
                            }
                        }}
                        multiple={false}
                        accept={allowedTypes.join(COMMA)}
                        disabled={disabled}
                    >
                        {({
                            getRootProps,
                            getInputProps,
                            isDragActive,
                            isDragAccept,
                            isDragReject
                        }: DropzoneState): ReactElement => (
                            <div
                                {...getRootProps({
                                    className: `fuc-dropzone ${isDragActive ? 'fuc-dragging' : EMPTY_STRING} ${
                                        disabled ? 'fuc-disabled' : EMPTY_STRING
                                    }`
                                })}
                                style={
                                    bgMode === BackgroundMode.PRIMARY && primaryColour
                                        ? {
                                              backgroundColor: primaryColour,
                                              color: colourContrast(primaryColour),
                                              borderColor: colourContrast(primaryColour)
                                          }
                                        : undefined
                                }
                            >
                                <input {...getInputProps()} />
                                <div className='fucd-dnd-labels'>
                                    <p className='fucddl-drop-area-label'>
                                        {isDragActive
                                            ? language.get(FileUploadLanguage.FileUpload_DropFileHere)
                                            : language.get(FileUploadLanguage.FileUpload_DragAndDrop)}
                                    </p>
                                    {isDragAccept && (
                                        <p className='fucddl-dnd-file-result'>
                                            {language.get(FileUploadLanguage.FileUpload_Accepted)}
                                        </p>
                                    )}
                                    {isDragReject && (
                                        <p className='fucddl-dnd-file-result'>
                                            {language.get(FileUploadLanguage.FileUpload_Rejected)}
                                        </p>
                                    )}
                                </div>
                                {previewDisplay(value)}
                                {isVarNotEmpty(value) &&
                                (value instanceof File || typeof value === 'string') &&
                                !disabled ? (
                                    <IconButton
                                        customClassName='fuc-delete-button'
                                        iconName={IconName.X}
                                        type={IconButtonType.BUTTON}
                                        onClick={(event?: React.MouseEvent): void => {
                                            event?.preventDefault()
                                            event?.stopPropagation()
                                            onChange(EMPTY_STRING)
                                        }}
                                    />
                                ) : (
                                    <></>
                                )}
                            </div>
                        )}
                    </Dropzone>
                </section>
            )}
        />
    )
}
